import { useEffect, useRef, useCallback, useState } from "react";

/**
 * usePresenceWebSocket — extends the public WS hook with presence + typing state.
 *
 * Returns:
 *   { connected, peers, typingActors, sendTyping(isTyping) }
 *
 * Manages presence_snapshot, presence_join, presence_leave, typing_start, typing_stop.
 * Forwards all other events to onMessage.
 */
export function usePresenceWebSocket(path, onMessage) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]); // [{actor_name, actor_role}]
  const [typingActors, setTypingActors] = useState([]); // [{actor_name, actor_role}]
  const reconnectRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const lastTypingRef = useRef(false);
  const typingTimeoutsRef = useRef({});

  const sendTyping = useCallback((isTyping) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (lastTypingRef.current === isTyping) return;
    lastTypingRef.current = isTyping;
    try {
      ws.send(JSON.stringify({ type: isTyping ? "typing_start" : "typing_stop" }));
    } catch {}
  }, []);

  const connect = useCallback(() => {
    if (!path) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, "ws") + path;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        const ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30000);
        ws._pingInterval = ping;
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "pong") return;
          if (data.type === "presence_snapshot") {
            setPeers(data.data?.peers || []);
            return;
          }
          if (data.type === "presence_join") {
            setPeers((prev) => {
              const exists = prev.find((p) => p.actor_name === data.data.actor_name && p.actor_role === data.data.actor_role);
              return exists ? prev : [...prev, data.data];
            });
            return;
          }
          if (data.type === "presence_leave") {
            setPeers((prev) => prev.filter((p) => !(p.actor_name === data.data.actor_name && p.actor_role === data.data.actor_role)));
            return;
          }
          if (data.type === "typing_start") {
            const key = `${data.data.actor_role}:${data.data.actor_name}`;
            setTypingActors((prev) => {
              const exists = prev.find((p) => `${p.actor_role}:${p.actor_name}` === key);
              return exists ? prev : [...prev, data.data];
            });
            // Auto-clear after 4s if no typing_stop arrives
            clearTimeout(typingTimeoutsRef.current[key]);
            typingTimeoutsRef.current[key] = setTimeout(() => {
              setTypingActors((prev) => prev.filter((p) => `${p.actor_role}:${p.actor_name}` !== key));
            }, 4000);
            return;
          }
          if (data.type === "typing_stop") {
            const key = `${data.data.actor_role}:${data.data.actor_name}`;
            clearTimeout(typingTimeoutsRef.current[key]);
            setTypingActors((prev) => prev.filter((p) => `${p.actor_role}:${p.actor_name}` !== key));
            return;
          }
          // Forward any other event
          if (onMessageRef.current) onMessageRef.current(data);
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        setPeers([]);
        setTypingActors([]);
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => { ws.close(); };
    } catch {}
  }, [path]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected, peers, typingActors, sendTyping };
}
